import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  getCookieConsent,
  saveCookieConsent,
  defaultCookiePreferences,
  hasConsent as checkConsent,
  acceptAllCookies,
  acceptNecessaryOnly,
  CookieCategories
} from '../utils/cookieConsent';

const CookieContext = createContext();

export const useCookieConsent = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieProvider');
  }
  return context;
};

export const CookieProvider = ({ children }) => {
  const [cookiePreferences, setCookiePreferences] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (consent) {
      setCookiePreferences(consent);
      setShowBanner(false);
    } else {
      setCookiePreferences(defaultCookiePreferences);
      setShowBanner(true);
    }
  }, []);

  const updatePreferences = useCallback((preferences) => {
    const saved = saveCookieConsent(preferences);
    if (saved) {
      setCookiePreferences(saved);
      setShowBanner(false);
      setShowSettings(false);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const saved = acceptAllCookies();
    if (saved) {
      setCookiePreferences(saved);
      setShowBanner(false);
      setShowSettings(false);
    }
  }, []);

  const acceptNecessary = useCallback(() => {
    const saved = acceptNecessaryOnly();
    if (saved) {
      setCookiePreferences(saved);
      setShowBanner(false);
      setShowSettings(false);
    }
  }, []);

  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  const hasConsent = useCallback((category) => {
    if (!cookiePreferences) return false;
    return cookiePreferences[category] === true;
  }, [cookiePreferences]);

  const value = {
    cookiePreferences,
    showBanner,
    showSettings,
    updatePreferences,
    acceptAll,
    acceptNecessary,
    openSettings,
    closeSettings,
    hasConsent,
    CookieCategories
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};
